'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { LottieIllustration } from '@/lib/ui-kit/lottie-illustration';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { Z_OVERLAY_MAX } from './constants';

interface TelegramRequiredDialogProps {
  open: boolean;
  mode: 'connect' | 'enable';
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isBusy?: boolean;
  ready?: boolean;
}

export function TelegramRequiredDialog({
  open,
  mode,
  onOpenChange,
  onConfirm,
  isBusy = false,
  ready = false,
}: TelegramRequiredDialogProps) {
  const tConnect = useTranslations('priceAlerts.telegramRequired');
  const tEnable = useTranslations('priceAlerts.telegramDisabled');
  const tCommon = useTranslations('common');
  const title = mode === 'connect' ? tConnect('title') : tEnable('title');
  const description = mode === 'connect' ? tConnect('description') : tEnable('description');
  const cancelLabel = mode === 'connect' ? tConnect('cancel') : tEnable('cancel');
  const confirmLabel = mode === 'connect' ? tConnect('connect') : tEnable('enable');

  // Auto-close once the user finishes the Telegram link flow (connect mode only).
  useEffect(() => {
    if (open && mode === 'connect' && ready) {
      onOpenChange(false);
    }
  }, [open, mode, ready, onOpenChange]);

  const z = Z_OVERLAY_MAX;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          style={{ zIndex: z }}
          className="fixed inset-0 bg-black/70 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          style={{ zIndex: z }}
          className={cn(
            'fixed top-1/2 left-1/2 w-[400px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2',
            'flex flex-col gap-6 rounded-2xl border border-v2-border bg-v2-surface-primary p-6 outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            // Stagger the content in like the shared DialogContent does, so the
            // children fade/slide instead of just popping in with the container.
            '[&>*:not([data-slot=dialog-close])]:animate-in [&>*:not([data-slot=dialog-close])]:fade-in',
            '[&>*:not([data-slot=dialog-close])]:slide-in-from-left-8 [&>*:not([data-slot=dialog-close])]:duration-500',
          )}
        >
          <DialogPrimitive.Close
            data-slot="dialog-close"
            aria-label={tCommon('close')}
            className="absolute top-4 right-4 flex size-10 cursor-pointer items-center justify-center text-v2-text-secondary outline-none transition-opacity hover:opacity-80"
          >
            <Icon name="Close" size={24} />
          </DialogPrimitive.Close>

          {/* Same illustration treatment as DialogEnableTelegramNotifications: the
              Lottie canvas is scaled up to give the badge mockup-size weight, then
              cropped, with a radial mask fading the (transparent) glow edges. */}
          <div className="flex justify-center">
            <div
              className="flex h-[110px] items-center justify-center overflow-hidden"
              style={{
                maskImage: 'radial-gradient(ellipse at center, #000 68%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, #000 68%, transparent 100%)',
              }}
            >
              <LottieIllustration name="server-error" size={250} className="-mb-4 shrink-0" />
            </div>
          </div>

          <DialogPrimitive.Title className="text-center text-[24px] leading-8 font-semibold text-v2-text-primary">
            {title}
          </DialogPrimitive.Title>

          <DialogPrimitive.Description className="text-center text-[13px] leading-4 font-normal tracking-[-0.13px] text-v2-text-secondary">
            {description}
          </DialogPrimitive.Description>

          <div className="flex gap-3">
            <Button
              type="button"
              appearance="neutral"
              size="M"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              appearance="accent"
              size="M"
              className="flex-1"
              onClick={onConfirm}
              disabled={isBusy}
              leftIcon={isBusy && <Loader2 className="size-5 animate-spin" />}
            >
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
